/**
 * ToastNotification
 *
 * 포그라운드 상태에서 알림을 표시하는 Toast 메시지 컴포넌트
 * 제목, 본문, 아이콘, 액션 버튼을 포함하며 자동으로 닫힙니다.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './ToastNotification.css';

const ToastNotification = ({
  title,
  body,
  icon,
  data,
  onClose,
  onClick,
  duration = 5000,
  position = 'top-right',
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // 자동 닫힘 타이머 설정
    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        handleClose();
      }, duration);
    }

    // 컴포넌트 언마운트 시 타이머 정리
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [duration]);

  // Toast 닫기 핸들러
  const handleClose = () => {
    setIsExiting(true);

    // 애니메이션 완료 후 실제로 제거
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) {
        onClose();
      }
    }, 300); // CSS 애니메이션 시간과 일치
  };

  // Toast 클릭 핸들러
  const handleClick = () => {
    console.log('[ToastNotification] Toast 클릭됨:', data);

    // 타이머 정리
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // 클릭 콜백 호출
    if (onClick) {
      onClick(data);
    }

    // Toast 닫기
    handleClose();
  };

  // 마우스 오버 시 타이머 일시 정지
  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  // 마우스 아웃 시 타이머 재시작
  const handleMouseLeave = () => {
    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        handleClose();
      }, 2000); // 남은 시간 대신 2초로 설정
    }
  };

  // 보이지 않는 경우 렌더링하지 않음
  if (!isVisible) {
    return null;
  }

  // 아이콘 URL 처리
  const iconUrl = icon || '/images/checkmark-256.png';

  return (
    <div
      className={`toast-notification toast-notification-${position} ${isExiting ? 'toast-notification-exit' : 'toast-notification-enter'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Toast 컨테이너 */}
      <div className="toast-notification-container">
        {/* 아이콘 */}
        <div className="toast-notification-icon">
          <img src={iconUrl} alt="알림 아이콘" />
        </div>

        {/* 콘텐츠 */}
        <div className="toast-notification-content">
          {/* 제목 */}
          <div className="toast-notification-title">{title}</div>

          {/* 본문 */}
          <div className="toast-notification-body">{body}</div>

          {/* 액션 버튼 */}
          {onClick && (
            <button
              className="toast-notification-action"
              onClick={handleClick}
            >
              자세히 보기
            </button>
          )}
        </div>

        {/* 닫기 버튼 */}
        <button
          className="toast-notification-close"
          onClick={handleClose}
          aria-label="닫기"
        >
          ×
        </button>
      </div>

      {/* 진행 바 (선택 사항) */}
      {duration > 0 && (
        <div
          className="toast-notification-progress"
          style={{
            animationDuration: `${duration}ms`,
          }}
        />
      )}
    </div>
  );
};

ToastNotification.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  icon: PropTypes.string,
  data: PropTypes.object,
  onClose: PropTypes.func,
  onClick: PropTypes.func,
  duration: PropTypes.number,
  position: PropTypes.oneOf([
    'top-left',
    'top-center',
    'top-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ]),
};

ToastNotification.defaultProps = {
  icon: null,
  data: null,
  onClose: null,
  onClick: null,
  duration: 5000,
  position: 'top-right',
};

export default ToastNotification;
